// frontend/src/pages/manufacturing/CustomerOrderManagement.js

import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, LinearProgress, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, 
  Alert, Collapse, List, ListItem, ListItemText, FormControl,
  InputLabel, Select, MenuItem, Divider, Autocomplete, CircularProgress,
  FormControlLabel, Checkbox, InputAdornment, Pagination
} from '@mui/material';
import {
  ShoppingCart, LocalShipping, Build, Schedule, 
  ExpandMore, ExpandLess, Visibility, CheckCircle,
  Add, Warning, Refresh, Factory, Upload, Delete, Inventory,
  FilterList, Clear, Search, Error as ErrorIcon
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import BulkSendToV2Dialog from './v2/components/BulkSendToV2Dialog';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';

// Debounce hook for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function CustomerOrderManagement() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [createDialog, setCreateDialog] = useState(false);
  
  // Filter states
  const [hideComplete, setHideComplete] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(25);
  
  // Work order creation queue
  const [processingQueue, setProcessingQueue] = useState(new Set());
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [bulkV2Open, setBulkV2Open] = useState(false);
  
  // Missing operations error dialog
  const [missingOpsDialog, setMissingOpsDialog] = useState({
    open: false,
    orderNumber: '',
    items: [],
    totalItems: 0,
    parentItem: '',  // NEW: top level BOM item
    isMultiOrder: false,
    affectedOrders: []
  });
  
  // Debounced search (waits 500ms after user stops typing)
  const debouncedSearch = useDebounce(searchQuery, 500);
  
  const [stats, setStats] = useState({
    total_active: 0,
    due_this_week: 0,
    overdue: 0,
    ready_to_ship: 0
  });

  usePageTitle(searchQuery ? `Orders: ${searchQuery}` : 'Orders');

  // Load orders whenever filters or page changes
  useEffect(() => {
    loadOrders();
  }, [page, debouncedSearch, statusFilter, hideComplete]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page: page,
        page_size: pageSize
      };

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (hideComplete) {
        params.hide_complete = 'true';
      }

      const response = await manufacturingAPI.customerOrders.list(params);
      setOrders(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load customer orders');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.dashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleHideCompleteChange = (event) => {
    setHideComplete(event.target.checked);
    setPage(1);
  };

  const clearFilters = () => {
    setHideComplete(false);
    setStatusFilter('all');
    setSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    setExpandedRows(new Set());
  };

  const orderNeedsProduction = (order) => {
    if (order.status !== 'new' && order.status !== 'confirmed') return false;
    
    return order.lines?.some(line => {
      const allocated = line.quantity_allocated || 0;
      const shipped = line.quantity_shipped || 0;
      const needed = line.quantity_ordered - allocated - shipped;
      return needed > 0;
    });
  };

  const ordersNeedingWorkOrders = orders.filter(orderNeedsProduction);

  const handleCreateWorkOrders = async (order) => {
    setProcessingQueue(prev => new Set(prev).add(order.id));
    
    try {
      const response = await manufacturingAPI.customerOrders.createWorkOrders(order.id);
      
      const data = response.data;
      const created = data.created || 0;
      const consolidated = data.consolidated || 0;
      
      let message = '';
      if (created > 0 && consolidated > 0) {
        message = `${order.order_number}: Created ${created} new, updated ${consolidated} existing`;
      } else if (created > 0) {
        message = `${order.order_number}: Created ${created} work orders`;
      } else if (consolidated > 0) {
        message = `${order.order_number}: Updated ${consolidated} existing work orders`;
      } else {
        message = `${order.order_number}: ${data.message || 'Work orders processed'}`;
      }
      
      toast.success(message, { autoClose: 3000 });
      return { success: true, orderId: order.id };
    } catch (error) {
      const errorData = error.response?.data;
      
      // Check if this is a missing operations error
      if (errorData?.items_missing_operation && errorData.items_missing_operation.length > 0) {
        setMissingOpsDialog({
          open: true,
          orderNumber: order.order_number,
          items: errorData.items_missing_operation,
          totalItems: errorData.total_items_to_manufacture || 0,
          parentItem: errorData.failed_for_item || '',  // Capture the parent item
          isMultiOrder: false,
          affectedOrders: []
        });
        return { 
          success: false, 
          orderId: order.id, 
          error: 'missing_operations',
          items: errorData.items_missing_operation,
          parentItem: errorData.failed_for_item
        };
      }
      
      // Generic error
      const errorMsg = errorData?.error || errorData?.message || 'Failed to create work orders';
      console.error(`[Create Work Orders] ${order.order_number} failed:`, {
        status: error.response?.status,
        errorMessage: errorMsg,
        responseData: errorData,
        originalError: error,
      });
      toast.error(`${order.order_number}: ${errorMsg}`);
      return { success: false, orderId: order.id, error: errorMsg };
    } finally {
      setProcessingQueue(prev => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  };

  // Update handleCreateAllWorkOrders to aggregate parent items
  const handleCreateAllWorkOrders = async () => {
    if (ordersNeedingWorkOrders.length === 0) {
      toast.info('No orders need work orders created');
      return;
    }
    
    setIsProcessingAll(true);
    setProcessingQueue(new Set(ordersNeedingWorkOrders.map(o => o.id)));
    
    let successCount = 0;
    let failCount = 0;
    let missingOpsErrors = [];
    
    for (const order of ordersNeedingWorkOrders) {
      const result = await handleCreateWorkOrders(order);
      if (result.success) {
        successCount++;
      } else {
        failCount++;
        if (result.error === 'missing_operations') {
          missingOpsErrors.push({
            orderNumber: order.order_number,
            items: result.items,
            parentItem: result.parentItem
          });
        }
      }
    }
    
    setIsProcessingAll(false);
    loadOrders();
    loadStats();
    
    // Show summary
    if (missingOpsErrors.length > 0) {
      // Aggregate all missing items across orders
      const allMissingItems = [];
      const seenCodes = new Set();
      const parentItems = new Set();
      
      missingOpsErrors.forEach(err => {
        if (err.parentItem) {
          parentItems.add(err.parentItem);
        }
        err.items.forEach(item => {
          if (!seenCodes.has(item.item_code)) {
            seenCodes.add(item.item_code);
            allMissingItems.push(item);
          }
        });
      });
      
      setMissingOpsDialog({
        open: true,
        orderNumber: `${missingOpsErrors.length} orders`,
        items: allMissingItems,
        totalItems: allMissingItems.length,
        parentItem: Array.from(parentItems).join(', '),  // Join multiple parents
        isMultiOrder: true,
        affectedOrders: missingOpsErrors.map(e => e.orderNumber)
      });
    }
    
    if (failCount === 0) {
      toast.success(`All ${successCount} orders processed successfully`, { autoClose: 5000 });
    } else if (missingOpsErrors.length === 0) {
      toast.warning(`Completed: ${successCount} succeeded, ${failCount} failed`, { autoClose: 5000 });
    }
  };

  const toggleRowExpansion = (orderId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'info',
      'confirmed': 'primary',
      'in_production': 'warning',
      'ready_to_ship': 'success',
      'partially_shipped': 'primary',
      'complete': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'New',
      'confirmed': 'Confirmed',
      'in_production': 'In Production',
      'ready_to_ship': 'Ready to Ship',
      'partially_shipped': 'Partially Shipped',
      'complete': 'Complete',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const hasActiveFilters = hideComplete || statusFilter !== 'all' || searchQuery.trim();
  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Customer Order Management</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart color="primary" />
            Customer Order Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Track customer orders from placement through production to shipment
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Factory />}
            onClick={() => setBulkV2Open(true)}
            disabled={isProcessingAll || processingQueue.size > 0}
            data-testid="btn-create-all-v2"
          >
            Create All Work Orders (V2)
          </Button>
          {ordersNeedingWorkOrders.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={isProcessingAll ? <CircularProgress size={20} /> : <Factory />}
              onClick={handleCreateAllWorkOrders}
              disabled={isProcessingAll || processingQueue.size > 0 || bulkV2Open}
            >
              {isProcessingAll 
                ? `Processing ${processingQueue.size}/${ordersNeedingWorkOrders.length}...`
                : `Create All Work Orders (${ordersNeedingWorkOrders.length})`
              }
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              loadOrders();
              loadStats();
            }}
            disabled={isProcessingAll}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setCreateDialog(true)}
            disabled={isProcessingAll}
          >
            New Customer Order
          </Button>
        </Box>
      </Box>

      {/* Processing Queue Alert */}
      {processingQueue.size > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>
              Creating work orders for {processingQueue.size} order{processingQueue.size > 1 ? 's' : ''}...
              {isProcessingAll && ` (${ordersNeedingWorkOrders.length - processingQueue.size} of ${ordersNeedingWorkOrders.length} complete)`}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Active Orders</Typography>
              <Typography variant="h4">{stats.total_active}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                In production pipeline
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Due This Week</Typography>
              <Typography variant="h4" color="warning.main">{stats.due_this_week}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Need attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Overdue</Typography>
              <Typography variant="h4" color="error.main">{stats.overdue}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Past due date
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Ready to Ship</Typography>
              <Typography variant="h4" color="success.main">{stats.ready_to_ship}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Production complete
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {stats.overdue > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{stats.overdue} orders are overdue!</strong> These need immediate attention.
        </Alert>
      )}

      {/* Search and Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FilterList color="primary" />
            <Typography variant="h6">Search & Filters</Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search orders, customers, products..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                          setPage(1);
                        }}
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="in_production">In Production</MenuItem>
                  <MenuItem value="ready_to_ship">Ready to Ship</MenuItem>
                  <MenuItem value="partially_shipped">Partially Shipped</MenuItem>
                  <MenuItem value="complete">Complete</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideComplete}
                    onChange={handleHideCompleteChange}
                    color="primary"
                  />
                }
                label="Hide Complete & Cancelled"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              {hasActiveFilters && (
                <Button
                  fullWidth
                  size="small"
                  startIcon={<Clear />}
                  onClick={clearFilters}
                  variant="outlined"
                >
                  Clear All
                </Button>
              )}
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing <strong>{orders.length}</strong> of <strong>{totalCount}</strong> orders on page {page}
              {searchQuery && ` - Search: "${searchQuery}"`}
              {hideComplete && " - Hiding complete & cancelled"}
              {statusFilter !== 'all' && ` - Status: ${getStatusLabel(statusFilter)}`}
            </Typography>
            
            {loading && (
              <CircularProgress size={20} />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell width={100}>Order #</TableCell>
              <TableCell width={140}>Customer</TableCell>
              <TableCell sx={{ minWidth: 280 }}>Products</TableCell>
              <TableCell align="center" width={80}>Quantity</TableCell>
              <TableCell align="center" width={130}>Progress</TableCell>
              <TableCell width={100}>Due Date</TableCell>
              <TableCell width={110}>Status</TableCell>
              <TableCell width={90}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {searchQuery 
                      ? `No orders found matching "${searchQuery}"`
                      : 'No orders found matching the current filters'
                    }
                  </Typography>
                  {hasActiveFilters && (
                    <Button
                      size="small"
                      startIcon={<Clear />}
                      onClick={clearFilters}
                      sx={{ mt: 2 }}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <React.Fragment key={order.id}>
                  <TableRow 
                    sx={{ 
                      '&:hover': { backgroundColor: 'action.hover' },
                      opacity: order.status === 'complete' || order.status === 'cancelled' ? 0.7 : 1,
                      borderLeft: 
                        order.status === 'complete' 
                          ? '4px solid' 
                          : order.status === 'cancelled'
                          ? '4px solid'
                          : (order.days_until_due < 0 && 
                            order.status !== 'complete' && 
                            order.status !== 'cancelled')
                          ? '4px solid'
                          : '4px solid transparent',
                      borderLeftColor: 
                        order.status === 'complete' 
                          ? 'success.main'
                          : order.status === 'cancelled'
                          ? 'grey.400'
                          : order.days_until_due < 0
                          ? 'error.main'
                          : 'transparent',
                      backgroundColor: processingQueue.has(order.id) ? 'action.selected' : 'inherit',
                    }}
                  >
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => toggleRowExpansion(order.id)}
                      >
                        {expandedRows.has(order.id) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>

                    <TableCell
                      onClick={() => navigate(`/manufacturing/customer-orders/${order.id}`)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {order.order_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(order.order_date)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {order.customer_name}
                      </Typography>
                      {order.customer_reference && (
                        <Typography variant="caption" color="text.secondary">
                          Ref: {order.customer_reference}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {(() => {
                        const stockItems = order.special_codes?.filter(
                          sc => sc.code_type_display === 'STOCKITEM'
                        ) || [];
                        const hasLines = order.lines && order.lines.length > 0;
                        const hasStockItems = stockItems.length > 0;

                        if (!hasLines && !hasStockItems) {
                          return <Typography variant="body2" color="text.secondary">No items</Typography>;
                        }

                        return (
                          <Box>
                            {hasLines && order.lines.map((line, index) => (
                              <Box key={line.id} sx={{ mb: (index < order.lines.length - 1 || hasStockItems) ? 1 : 0 }}>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  {line.product_display} x {line.quantity_ordered}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {line.product_name}
                                </Typography>
                                {line.quantity_allocated > 0 && (
                                  <Chip
                                    label={`${line.quantity_allocated} allocated`}
                                    size="small"
                                    color="info"
                                    sx={{ ml: 1, height: 20 }}
                                    icon={<Inventory />}
                                  />
                                )}
                                {line.quantity_shipped > 0 && (
                                  <Chip
                                    label={`${line.quantity_shipped} shipped`}
                                    size="small"
                                    color="success"
                                    sx={{ ml: 1, height: 20 }}
                                  />
                                )}
                              </Box>
                            ))}
                            {stockItems.map((sc, idx) => (
                              <Box key={`stockitem-${sc.id ?? idx}`} sx={{ mb: idx < stockItems.length - 1 ? 1 : 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip
                                    label="STOCKITEM"
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    {sc.value}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>

                    <TableCell align="center">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {order.total_quantity_shipped || 0} / {order.total_quantity_ordered || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Shipped / Ordered
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={order.completion_percentage || 0} 
                          sx={{ width: 100, height: 8 }}
                          color={order.completion_percentage >= 100 ? 'success' : 'primary'}
                        />
                        <Typography variant="body2">
                          {Math.round(order.completion_percentage || 0)}%
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={order.days_until_due < 0 ? 'error.main' : 'text.primary'}
                        fontWeight={order.days_until_due < 0 ? 'bold' : 'normal'}
                      >
                        {formatDate(order.due_date)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.days_until_due < 0 
                          ? `${Math.abs(order.days_until_due)} days overdue`
                          : order.days_until_due === 0
                          ? 'Due today'
                          : `${order.days_until_due} days left`
                        }
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip 
                        label={getStatusLabel(order.status)} 
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                      {processingQueue.has(order.id) && (
                        <Chip 
                          label="Processing..." 
                          size="small" 
                          color="warning"
                          sx={{ ml: 1 }}
                          icon={<CircularProgress size={12} />}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {(() => {
                        if (order.status === 'complete' || order.status === 'cancelled') {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircle color="success" />
                              <Typography variant="caption" color="success.main">
                                {order.status === 'complete' ? 'Complete' : 'Cancelled'}
                              </Typography>
                            </Box>
                          );
                        }

                        const needsProduction = orderNeedsProduction(order);

                        const hasItemsReadyToShip = order.lines?.some(line => {
                          const allocated = line.quantity_allocated || 0;
                          const completed = line.quantity_completed || 0;
                          const shipped = line.quantity_shipped || 0;
                          const totalAvailable = allocated + completed;
                          
                          return totalAvailable > 0 && totalAvailable > shipped;
                        });

                        return (
                          <>
                            {needsProduction && (order.status === 'new' || order.status === 'confirmed') && (
                              <Tooltip title={
                                processingQueue.has(order.id) 
                                  ? "Creating Work Orders..." 
                                  : "Create Work Orders for Items Needing Production"
                              }>
                                <span>
                                  <IconButton 
                                    size="small"
                                    color="primary"
                                    onClick={() => handleCreateWorkOrders(order)}
                                    disabled={processingQueue.has(order.id) || isProcessingAll}
                                  >
                                    {processingQueue.has(order.id) ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <Factory />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {hasItemsReadyToShip && (
                              <Tooltip title="Ship Available Items">
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={() => navigate(`/manufacturing/customer-orders/${order.id}`)}
                                >
                                  <LocalShipping />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row - Detailed View */}
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0 }}>
                      <Collapse in={expandedRows.has(order.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Grid container spacing={2}>
                            {/* Order Line Items with Allocation Status */}
                            <Grid item xs={12}>
                              <Typography variant="h6" gutterBottom>
                                Order Line Items
                              </Typography>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Line</TableCell>
                                      <TableCell>Product</TableCell>
                                      <TableCell align="right">Ordered</TableCell>
                                      <TableCell align="right">Allocated</TableCell>
                                      <TableCell align="right">Need to Produce</TableCell>
                                      <TableCell align="right">Completed</TableCell>
                                      <TableCell align="right">Shipped</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {order.lines && order.lines.map((line) => (
                                      <TableRow key={line.id}>
                                        <TableCell>{line.line_number}</TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight="bold">
                                            {line.product_display}
                                          </Typography>
                                          <Typography variant="caption">
                                            {line.product_name}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" fontWeight="bold">
                                            {line.quantity_ordered}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Chip 
                                            label={line.quantity_allocated || 0}
                                            size="small"
                                            color={
                                              (line.quantity_allocated || 0) >= line.quantity_ordered 
                                                ? 'success' 
                                                : (line.quantity_allocated || 0) > 0 
                                                ? 'warning' 
                                                : 'default'
                                            }
                                            icon={<Inventory />}
                                          />
                                          <Typography variant="caption" display="block" color="text.secondary">
                                            from stock
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Chip 
                                            label={line.quantity_needed_from_production !== undefined 
                                              ? line.quantity_needed_from_production 
                                              : (line.quantity_ordered - (line.quantity_allocated || 0))}
                                            size="small"
                                            color={
                                              (line.quantity_needed_from_production || (line.quantity_ordered - (line.quantity_allocated || 0))) > 0 
                                                ? 'error' 
                                                : 'success'
                                            }
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography 
                                            variant="body2" 
                                            color={line.quantity_completed >= line.quantity_ordered ? 'success.main' : 'text.primary'}
                                          >
                                            {line.quantity_completed || 0}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">{line.quantity_shipped}</TableCell>
                                        <TableCell>
                                          {line.quantity_shipped >= line.quantity_ordered ? (
                                            <Chip label="Complete" size="small" color="success" />
                                          ) : (line.quantity_allocated || 0) + (line.quantity_completed || 0) >= line.quantity_ordered ? (
                                            <Chip label="Ready to Ship" size="small" color="warning" />
                                          ) : line.quantity_completed > 0 ? (
                                            <Chip label="In Progress" size="small" color="info" />
                                          ) : (line.quantity_allocated || 0) > 0 ? (
                                            <Chip label="Partially Allocated" size="small" color="info" />
                                          ) : (
                                            <Chip label="Pending" size="small" />
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>

                            {/* Stock Allocation Summary */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="h6" gutterBottom>
                                <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Stock Allocation Summary
                              </Typography>
                              <Card variant="outlined">
                                <CardContent>
                                  {order.lines && order.lines.map((line) => {
                                    const allocated = line.quantity_allocated || 0;
                                    const needed = line.quantity_needed_from_production !== undefined 
                                      ? line.quantity_needed_from_production 
                                      : (line.quantity_ordered - allocated);
                                    
                                    return (
                                      <Box key={line.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { mb: 0, pb: 0, borderBottom: 'none' } }}>
                                        <Typography variant="body2" fontWeight="bold">
                                          {line.product_display}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Allocated from Stock
                                            </Typography>
                                            <Typography variant="h6" color={allocated > 0 ? 'success.main' : 'text.secondary'}>
                                              {allocated}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Need to Produce
                                            </Typography>
                                            <Typography variant="h6" color={needed > 0 ? 'error.main' : 'success.main'}>
                                              {needed}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Total Ordered
                                            </Typography>
                                            <Typography variant="h6">
                                              {line.quantity_ordered}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* Production Status */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="h6" gutterBottom>
                                Production Status
                              </Typography>
                              <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2">
                                  Total Work Orders: <strong>{order.production_status?.total_work_orders || 0}</strong>
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                  Completed: <strong>{order.production_status?.completed || 0}</strong>
                                </Typography>
                                <Typography variant="body2" color="warning.main">
                                  In Progress: <strong>{order.production_status?.in_progress || 0}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Pending: <strong>{order.production_status?.pending || 0}</strong>
                                </Typography>
                              </Box>
                              
                              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Estimated Completion
                              </Typography>
                              <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                {order.estimated_completion_date ? (
                                  <>
                                    <Typography variant="body2">
                                      Expected: <strong>{formatDate(order.estimated_completion_date)}</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Based on current work order schedules
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No active work orders
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="h6" gutterBottom>
                            Related Work Orders
                          </Typography>
                          <WorkOrdersList 
                            customerRef={order.customer_reference || order.order_number} 
                            orderId={order.id}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination 
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            disabled={loading}
          />
        </Box>
      )}

      {/* Create Dialog */}
      <CreateCustomerOrderDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        onSuccess={() => {
          setCreateDialog(false);
          loadOrders();
          loadStats();
        }}
      />

      {/* Missing Operations Error Dialog */}
      <MissingOperationsDialog
        open={missingOpsDialog.open}
        onClose={() => setMissingOpsDialog({ ...missingOpsDialog, open: false })}
        orderNumber={missingOpsDialog.orderNumber}
        items={missingOpsDialog.items}
        totalItems={missingOpsDialog.totalItems}
        parentItem={missingOpsDialog.parentItem}
        isMultiOrder={missingOpsDialog.isMultiOrder}
        affectedOrders={missingOpsDialog.affectedOrders}
      />

      {/* Bulk "Create All Work Orders (V2)" progress + report dialog */}
      <BulkSendToV2Dialog
        open={bulkV2Open}
        onClose={() => setBulkV2Open(false)}
        onDone={() => {
          loadOrders();
          loadStats();
        }}
      />
    </Box>
  );
}

// Missing Operations Error Dialog Component
function MissingOperationsDialog({ 
  open, 
  onClose, 
  orderNumber, 
  items, 
  totalItems,
  parentItem,
  isMultiOrder,
  affectedOrders 
}) {
  const navigate = useNavigate();

  const handleGoToBOM = () => {
    // Navigate to BOM page with search pre-filled
    if (parentItem && !parentItem.includes(',')) {
      navigate(`/manufacturing/bom?search=${encodeURIComponent(parentItem)}`);
    } else {
      navigate('/manufacturing/bom');
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ErrorIcon color="error" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" color="error">
              Cannot Create Work Orders
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isMultiOrder 
                ? `${affectedOrders?.length} orders affected`
                : `Order: ${orderNumber}`
              }
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{items.length} item{items.length > 1 ? 's' : ''}</strong> in the BOM tree 
            {items.length > 1 ? ' do ' : ' does '} 
            not have an assembly operation assigned.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Work orders cannot be created until all manufactured items have operations assigned.
          </Typography>
        </Alert>

        {/* Parent Item Info */}
        {parentItem && (
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 3, 
              bgcolor: 'primary.50', 
              borderColor: 'primary.main',
              borderWidth: 2
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="primary.main" gutterBottom>
                Top Level BOM Item
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                {parentItem}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Search for this item in the BOM page to find and fix the child items below.
              </Typography>
            </CardContent>
          </Card>
        )}

        {isMultiOrder && affectedOrders && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Affected Orders:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {affectedOrders.map((orderNum) => (
                <Chip key={orderNum} label={orderNum} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Child Items Missing Assembly Operation:
        </Typography>
        
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Item Code</strong></TableCell>
                <TableCell><strong>Item Name</strong></TableCell>
                <TableCell align="right"><strong>Qty Needed</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow 
                  key={item.item_id || index}
                  sx={{ 
                    '&:hover': { backgroundColor: 'action.hover' },
                    backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {item.item_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.item_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={item.quantity_needed} 
                      size="small" 
                      color="warning"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Instructions */}
        <Alert severity="info" sx={{ mt: 3 }} icon={<Build />}>
          <Typography variant="body2" gutterBottom>
            <strong>How to fix:</strong>
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <li>
              Go to the <strong>Bill of Materials</strong> page
            </li>
            <li>
              Search for the parent item: <strong style={{ fontFamily: 'monospace' }}>{parentItem || 'the top-level item'}</strong>
            </li>
            <li>
              Find the child items listed above in the BOM tree
            </li>
            <li>
              Click <strong>Edit</strong> on each child row to set the <strong>Operation</strong> field
            </li>
            <li>
              Return here and try creating work orders again
            </li>
          </Box>
        </Alert>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Why is this required?</strong> Each manufactured item in the BOM needs an operation 
            (work center/process) assigned so the system knows which department or machine 
            should produce it. Operations are typically set during BOM import from Sage via the AG_ID field,
            or can be manually assigned in the BOM editor.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          variant="contained"
          color="primary"
          startIcon={<Build />}
          onClick={handleGoToBOM}
        >
          Go to BOM Page
          {parentItem && !parentItem.includes(',') && (
            <Typography variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
              (Search: {parentItem})
            </Typography>
          )}
        </Button>
        <Button 
          variant="outlined"
          onClick={() => {
            navigate('/manufacturing/operations');
            onClose();
          }}
        >
          View Operations
        </Button>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
// Sub-component to show work orders for a customer order
function WorkOrdersList({ customerRef, orderId }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customerRef) {
      loadWorkOrders();
    }
  }, [customerRef]);

  const loadWorkOrders = async () => {
    try {
      const response = await manufacturingAPI.workOrders.list();
      const allWorkOrders = response.data.results || response.data;
      
      const refs = [customerRef];
      if (customerRef.includes('CO-')) {
        refs.push(customerRef.replace('CO-', ''));
      }
      
      const filtered = allWorkOrders.filter(wo => 
        refs.includes(wo.customer_order_ref) || 
        wo.customer_order_ref === customerRef
      );
      
      setWorkOrders(filtered);
    } catch (error) {
      console.error('Failed to load work orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LinearProgress />;

  if (workOrders.length === 0) {
    return (
      <Alert severity="info">
        No work orders created yet. Click the factory icon to create work orders.
      </Alert>
    );
  }

  return (
    <List dense>
      {workOrders.map((wo) => (
        <ListItem key={wo.id}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {wo.work_order_number}
                </Typography>
                <Typography variant="body2">
                  - {wo.output_item_display} ({wo.output_item_name})
                </Typography>
                <Chip 
                  label={wo.status} 
                  size="small" 
                  color={
                    wo.status === 'completed' ? 'success' : 
                    wo.status === 'in_progress' ? 'warning' : 
                    wo.status === 'released' ? 'info' :
                    'default'
                  }
                />
              </Box>
            }
            secondary={
              <Typography variant="caption">
                Qty: {wo.quantity_completed}/{wo.quantity_to_produce} | 
                Progress: {Math.round(wo.progress_percentage || 0)}% | 
                Priority: {wo.priority}
              </Typography>
            }
          />
          {wo.status === 'completed' && <CheckCircle color="success" />}
        </ListItem>
      ))}
    </List>
  );
}

// Create Customer Order Dialog with Autocomplete Search
function CreateCustomerOrderDialog({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_reference: '',
    sage_order_number: '',
    sage_customer_code: '',
    due_date: '',
    notes: ''
  });
  
  // Each line now has its own searchQuery and searchResults
  const [orderLines, setOrderLines] = useState([
    { product: null, quantity_ordered: '', unit_price: '', notes: '', searchQuery: '', searchResults: [], isLoading: false }
  ]);

  const searchItems = async (query, index) => {
    if (query.length < 2) {
      updateOrderLine(index, 'searchResults', []);
      return;
    }
    
    updateOrderLine(index, 'isLoading', true);
    try {
      const params = {
        page_size: 50,
        search: query
      };
      const response = await manufacturingAPI.items.list(params);
      const itemsData = response.data.results || response.data;
      updateOrderLine(index, 'searchResults', itemsData);
    } catch (error) {
      console.error('Error searching items:', error);
      toast.error('Failed to search products');
    } finally {
      updateOrderLine(index, 'isLoading', false);
    }
  };

  const addOrderLine = () => {
    setOrderLines([...orderLines, { product: null, quantity_ordered: '', unit_price: '', notes: '', searchQuery: '', searchResults: [], isLoading: false }]);
  };

  const removeOrderLine = (index) => {
    if (orderLines.length > 1) {
      setOrderLines(orderLines.filter((_, i) => i !== index));
    }
  };

  const updateOrderLine = (index, field, value) => {
    setOrderLines(prev => {
      const newLines = [...prev];
      newLines[index] = { ...newLines[index], [field]: value };
      return newLines;
    });
  };

  const handleSearchChange = (index, newInputValue) => {
    updateOrderLine(index, 'searchQuery', newInputValue);
    
    // Debounce the search
    clearTimeout(window[`searchTimeout_${index}`]);
    window[`searchTimeout_${index}`] = setTimeout(() => {
      searchItems(newInputValue, index);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validLines = orderLines.filter(line => line.product && line.quantity_ordered);
    if (validLines.length === 0) {
      toast.error('Please add at least one product with quantity');
      return;
    }
    
    try {
      const orderData = {
        ...formData,
        lines: validLines.map((line, index) => ({
          line_number: index + 1,
          product: line.product.id,
          quantity_ordered: parseInt(line.quantity_ordered),
          unit_price: line.unit_price ? parseFloat(line.unit_price) : null,
          notes: line.notes
        }))
      };
      
      await manufacturingAPI.customerOrders.create(orderData);
      toast.success('Customer order created successfully');
      onSuccess();
      
      // Reset form
      setFormData({
        customer_name: '',
        customer_reference: '',
        sage_order_number: '',
        sage_customer_code: '',
        due_date: '',
        notes: ''
      });
      setOrderLines([{ product: null, quantity_ordered: '', unit_price: '', notes: '', searchQuery: '', searchResults: [], isLoading: false }]);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create customer order');
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      orderLines.forEach((_, index) => {
        clearTimeout(window[`searchTimeout_${index}`]);
      });
    };
  }, []);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        customer_name: '',
        customer_reference: '',
        sage_order_number: '',
        sage_customer_code: '',
        due_date: '',
        notes: ''
      });
      setOrderLines([{ product: null, quantity_ordered: '', unit_price: '', notes: '', searchQuery: '', searchResults: [], isLoading: false }]);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart color="primary" />
              Create Customer Order
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Order Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer Name *"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer Reference (PO#)"
                value={formData.customer_reference}
                onChange={(e) => setFormData({ ...formData, customer_reference: e.target.value })}
                placeholder="e.g., PO-12345"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sage Order Number"
                value={formData.sage_order_number}
                onChange={(e) => setFormData({ ...formData, sage_order_number: e.target.value })}
                placeholder="SO123456"
                helperText="For linking with Sage 50"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sage Customer Code"
                value={formData.sage_customer_code}
                onChange={(e) => setFormData({ ...formData, sage_customer_code: e.target.value })}
                placeholder="CUST001"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Due Date *"
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 2 }}>
            <Typography variant="h6">
              Order Lines
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={addOrderLine}
              startIcon={<Add />}
            >
              Add Line
            </Button>
          </Box>

          {orderLines.map((line, index) => (
            <Card key={index} sx={{ mb: 2, p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    value={line.product}
                    onChange={(event, newValue) => {
                      updateOrderLine(index, 'product', newValue);
                      if (newValue) {
                        updateOrderLine(index, 'searchQuery', `${newValue.item_code} - ${newValue.name}`);
                      }
                    }}
                    inputValue={line.searchQuery}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        handleSearchChange(index, newInputValue);
                      } else if (reason === 'clear') {
                        updateOrderLine(index, 'searchQuery', '');
                        updateOrderLine(index, 'searchResults', []);
                      }
                    }}
                    options={line.searchResults}
                    getOptionLabel={(option) => 
                      option ? `${option.item_code} - ${option.name}` : ''
                    }
                    loading={line.isLoading}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Product ${index === 0 ? '*' : ''}`}
                        required={index === 0}
                        placeholder="Type to search..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {line.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...restProps } = props;
                      return (
                        <li key={key} {...restProps}>
                          <Box>
                            <Typography variant="body2">
                              <strong>{option.item_code}</strong> - {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Type: {option.item_type.replace('_', ' ')} | 
                              UOM: {option.unit_of_measure}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                    noOptionsText={
                      line.searchQuery.length < 2 
                        ? "Type at least 2 characters to search" 
                        : "No products found"
                    }
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={`Quantity ${index === 0 ? '*' : ''}`}
                    type="number"
                    value={line.quantity_ordered}
                    onChange={(e) => updateOrderLine(index, 'quantity_ordered', e.target.value)}
                    required={index === 0}
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Unit Price"
                    type="number"
                    value={line.unit_price}
                    onChange={(e) => updateOrderLine(index, 'unit_price', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Line Notes"
                    value={line.notes}
                    onChange={(e) => updateOrderLine(index, 'notes', e.target.value)}
                    placeholder="Special instructions..."
                  />
                </Grid>

                <Grid item xs={12} md={1}>
                  {orderLines.length > 1 && (
                    <IconButton 
                      color="error" 
                      onClick={() => removeOrderLine(index)}
                      sx={{ ml: 1 }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Card>
          ))}

          <TextField
            fullWidth
            label="Order Notes"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="General order notes, delivery instructions, etc..."
            sx={{ mt: 2 }}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            After creating this order, stock will be automatically allocated and you can generate work orders for any shortage.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Create Order
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}